import { useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";

export default function PrivacyPolicy() {
  const [expandedSections, setExpandedSections] = useState({
    introduction: true,
    informationCollection: false,
    useOfInformation: false,
    dataSharing: false,
    dataRetention: false,
    cookies: false,
    userRights: false,
    dataProtection: false,
    minors: false,
    changes: false,
    contact: false,
  });

  const toggleSection = (section) => {
    setExpandedSections({
      ...expandedSections,
      [section]: !expandedSections[section],
    });
  };

  // Component for section headers with toggle functionality
  const SectionHeader = ({ id, title }) => (
    <div
      className="flex justify-between items-center cursor-pointer py-4 border-b border-gray-200"
      onClick={() => toggleSection(id)}
    >
      <h2 className="text-xl font-bold text-gray-800">{title}</h2>
      {expandedSections[id] ? (
        <ChevronUp className="h-5 w-5 text-gray-600" />
      ) : (
        <ChevronDown className="h-5 w-5 text-gray-600" />
      )}
    </div>
  );

  // Component for section content
  const SectionContent = ({ id, children }) => (
    <div
      className={`py-4 text-gray-700 ${
        expandedSections[id] ? "block" : "hidden"
      }`}
    >
      {children}
    </div>
  );

  return (
    <div className="max-w-4xl mx-auto p-6 bg-white rounded-lg shadow-lg">
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">RideMe</h1>
        <h2 className="text-2xl font-semibold text-gray-800">Privacy Policy</h2>
        <p className="text-gray-600 mt-2">Last Updated: April 16, 2025</p>
      </div>

      <div className="space-y-4">
        {/* Introduction */}
        <div>
          <SectionHeader id="introduction" title="1. Introduction" />
          <SectionContent id="introduction">
            <p className="mb-3">
              RideMe ("we," "our," or "us") is committed to protecting the
              privacy of our users. This Privacy Policy explains how we collect,
              use, disclose, and safeguard your information when you use our
              peer-to-peer car rental platform operating in Ghana.
            </p>
            <p className="mb-3">
              By accessing or using the RideMe platform, you consent to the
              collection, use, and sharing of your information as described in
              this Privacy Policy. If you do not agree with our policies and
              practices, please do not use our services.
            </p>
            <p>
              We encourage you to read this Privacy Policy carefully to
              understand our practices regarding your information and how we
              will treat it.
            </p>
          </SectionContent>
        </div>

        {/* Information Collection */}
        <div>
          <SectionHeader
            id="informationCollection"
            title="2. Information We Collect"
          />
          <SectionContent id="informationCollection">
            <p className="mb-3">
              We collect several types of information from and about users of
              our platform:
            </p>

            <h3 className="font-semibold text-lg mb-2">
              2.1 Personal Information
            </h3>
            <ul className="list-disc pl-5 space-y-2 mb-4">
              <li>
                <strong>Identity Information:</strong> Full name, date of birth,
                profile picture
              </li>
              <li>
                <strong>Contact Information:</strong> Email address, phone
                number, residential address
              </li>
              <li>
                <strong>Identification Documents:</strong> Ghana Card details,
                driving license information
              </li>
              <li>
                <strong>Account Information:</strong> Username, password,
                account preferences
              </li>
              <li>
                <strong>Payment Information:</strong> Banking details, mobile
                money information, payment card details
              </li>
            </ul>

            <h3 className="font-semibold text-lg mb-2">
              2.2 Vehicle Information (for Owners)
            </h3>
            <ul className="list-disc pl-5 space-y-2 mb-4">
              <li>Vehicle registration details</li>
              <li>Vehicle make, model, year, and specifications</li>
              <li>Vehicle photographs</li>
              <li>Roadworthy certification information</li>
              <li>Insurance documentation</li>
            </ul>

            <h3 className="font-semibold text-lg mb-2">
              2.3 Usage Information
            </h3>
            <ul className="list-disc pl-5 space-y-2 mb-4">
              <li>
                <strong>Transaction Data:</strong> Rental history, booking
                details, payment records
              </li>
              <li>
                <strong>Communication Data:</strong> Messages between users,
                communications with our customer support
              </li>
              <li>
                <strong>Review and Rating Data:</strong> Feedback provided about
                users or vehicles
              </li>
            </ul>

            <h3 className="font-semibold text-lg mb-2">
              2.4 Technical Information
            </h3>
            <ul className="list-disc pl-5 space-y-2 mb-4">
              <li>IP address</li>
              <li>Device information (type, operating system, browser)</li>
              <li>Log data (access times, pages viewed)</li>
              <li>Location data (with your permission)</li>
            </ul>

            <p>
              We collect this information when you create an account, list a
              vehicle, make a booking, communicate with other users, use our
              platform, contact our customer support, or otherwise interact with
              our services.
            </p>
          </SectionContent>
        </div>

        {/* Use of Information */}
        <div>
          <SectionHeader
            id="useOfInformation"
            title="3. How We Use Your Information"
          />
          <SectionContent id="useOfInformation">
            <p className="mb-3">
              We use the information we collect for various purposes, including:
            </p>

            <h3 className="font-semibold text-lg mb-2">
              3.1 Providing and Improving Our Services
            </h3>
            <ul className="list-disc pl-5 space-y-2 mb-4">
              <li>Processing and managing vehicle rentals</li>
              <li>Facilitating payments between users</li>
              <li>Authenticating user identities and verifying eligibility</li>
              <li>Providing customer support</li>
              <li>Improving and developing new features and services</li>
              <li>Analyzing platform usage and performance</li>
            </ul>

            <h3 className="font-semibold text-lg mb-2">
              3.2 Safety and Security
            </h3>
            <ul className="list-disc pl-5 space-y-2 mb-4">
              <li>Verifying user identities</li>
              <li>
                Preventing and detecting fraud, abuse, and other harmful
                activities
              </li>
              <li>Resolving disputes between users</li>
              <li>Enforcing our Terms and Conditions</li>
              <li>Complying with legal obligations</li>
            </ul>

            <h3 className="font-semibold text-lg mb-2">3.3 Communication</h3>
            <ul className="list-disc pl-5 space-y-2 mb-4">
              <li>
                Sending service-related notifications about your account or
                rentals
              </li>
              <li>Responding to your inquiries and requests</li>
              <li>Providing important updates about our services</li>
              <li>
                Facilitating communication between vehicle owners and renters
              </li>
            </ul>

            <h3 className="font-semibold text-lg mb-2">
              3.4 Marketing and Advertising
            </h3>
            <ul className="list-disc pl-5 space-y-2 mb-4">
              <li>
                Sending promotional communications about new features, special
                offers, or other information we think you may find interesting
              </li>
              <li>Tailoring content and advertisements to your interests</li>
              <li>Measuring the effectiveness of advertisements and content</li>
              <li>Conducting market research and surveys</li>
            </ul>

            <p>
              You may opt out of receiving marketing communications from us by
              following the unsubscribe instructions included in our emails or
              by contacting us directly.
            </p>
          </SectionContent>
        </div>

        {/* Data Sharing */}
        <div>
          <SectionHeader
            id="dataSharing"
            title="4. Information Sharing and Disclosure"
          />
          <SectionContent id="dataSharing">
            <p className="mb-3">
              We may share your information in the following circumstances:
            </p>

            <h3 className="font-semibold text-lg mb-2">
              4.1 With Other RideMe Users
            </h3>
            <ul className="list-disc pl-5 space-y-2 mb-4">
              <li>
                When owners and renters enter into a rental agreement, we share
                relevant contact information and booking details necessary to
                facilitate the rental.
              </li>
              <li>
                Public profile information (such as your name, profile photo,
                and reviews) is visible to other users on the platform.
              </li>
            </ul>

            <h3 className="font-semibold text-lg mb-2">
              4.2 With Service Providers
            </h3>
            <ul className="list-disc pl-5 space-y-2 mb-4">
              <li>Payment processors to facilitate transactions</li>
              <li>
                Insurance partners for coverage verification and claims
                processing
              </li>
              <li>Cloud service providers who host our data</li>
              <li>
                Technical service providers who help maintain and improve our
                platform
              </li>
            </ul>

            <h3 className="font-semibold text-lg mb-2">
              4.3 For Legal Purposes
            </h3>
            <ul className="list-disc pl-5 space-y-2 mb-4">
              <li>
                To comply with applicable laws, regulations, or legal processes
              </li>
              <li>
                To respond to lawful requests from public and governmental
                authorities
              </li>
              <li>
                To protect our rights, privacy, safety, or property, or that of
                our users or others
              </li>
              <li>
                To detect, prevent, or address fraud and other illegal
                activities
              </li>
            </ul>

            <h3 className="font-semibold text-lg mb-2">
              4.4 Business Transfers
            </h3>
            <p className="mb-3">
              If RideMe is involved in a merger, acquisition, or sale of all or
              a portion of its assets, your information may be transferred as
              part of that transaction. We will notify you via email and/or a
              prominent notice on our website of any change in ownership or uses
              of your information.
            </p>

            <p className="mb-3">
              We do not sell or rent your personal information to third parties
              for their marketing purposes without your explicit consent.
            </p>

            <p>
              Any third parties with whom we share your information are
              contractually obligated to maintain the confidentiality and
              security of your data and to process it in accordance with this
              Privacy Policy.
            </p>
          </SectionContent>
        </div>

        {/* Data Retention */}
        <div>
          <SectionHeader id="dataRetention" title="5. Data Retention" />
          <SectionContent id="dataRetention">
            <p className="mb-3">
              RideMe retains your personal information for as long as necessary
              to fulfill the purposes outlined in this Privacy Policy, including
              providing our services, complying with legal obligations,
              resolving disputes, and enforcing our agreements.
            </p>

            <p className="mb-3">
              Even after you close your account with RideMe, we retain your
              information in our database for the following reasons:
            </p>

            <ul className="list-disc pl-5 space-y-2 mb-4">
              <li>
                To comply with legal obligations, including tax, financial
                reporting, and audit requirements
              </li>
              <li>To resolve potential disputes or legal claims</li>
              <li>To detect and prevent fraud or other harmful activities</li>
              <li>
                To maintain business records for analysis and improvement of our
                services
              </li>
              <li>To honor the validity of rental histories and reviews</li>
            </ul>

            <p className="mb-3">
              If you wish to have your information deleted from our systems, you
              may submit a request as described in the "Your Rights and Choices"
              section below. We will consider such requests on a case-by-case
              basis, taking into account our legal obligations and legitimate
              business interests.
            </p>

            <p>
              We do not currently have specific data retention periods for
              different types of information, but we are committed to not
              keeping your personal information for longer than reasonably
              necessary for the purposes for which it was provided.
            </p>
          </SectionContent>
        </div>

        {/* Cookies */}
        <div>
          <SectionHeader
            id="cookies"
            title="6. Cookies and Tracking Technologies"
          />
          <SectionContent id="cookies">
            <p className="mb-3">
              While we do not currently use cookies or similar tracking
              technologies on our website or mobile application intentionally,
              our service providers may use such technologies to help us deliver
              better and more personalized service.
            </p>

            <p className="mb-3">
              These technologies may collect information about your online
              activities over time and across different websites or online
              services. This information helps us understand how users navigate
              through and use our platform, allowing us to improve our services
              and user experience.
            </p>

            <p className="mb-3">
              If we implement cookies or tracking technologies in the future, we
              will update this Privacy Policy to reflect those changes.
            </p>

            <p>
              You can set your browser to refuse all or some browser cookies or
              to alert you when cookies are being sent. If you disable or refuse
              cookies, please note that some parts of our platform may become
              inaccessible or not function properly.
            </p>
          </SectionContent>
        </div>

        {/* User Rights */}
        <div>
          <SectionHeader id="userRights" title="7. Your Rights and Choices" />
          <SectionContent id="userRights">
            <p className="mb-3">
              Under Ghanaian data protection laws, you have certain rights
              regarding your personal information. These include:
            </p>

            <ul className="list-disc pl-5 space-y-2 mb-4">
              <li>
                <strong>Access:</strong> You have the right to request copies of
                your personal information that we hold.
              </li>
              <li>
                <strong>Correction:</strong> You have the right to request that
                we correct any information you believe is inaccurate or
                incomplete.
              </li>
              <li>
                <strong>Restriction:</strong> You have the right to request that
                we restrict the processing of your personal information under
                certain circumstances.
              </li>
              <li>
                <strong>Data Portability:</strong> You have the right to request
                that we transfer the information we have collected to another
                organization, or directly to you, under certain conditions.
              </li>
              <li>
                <strong>Objection:</strong> You have the right to object to our
                processing of your personal information under certain
                circumstances.
              </li>
              <li>
                <strong>Withdrawal of Consent:</strong> Where we rely on your
                consent to process your personal information, you have the right
                to withdraw that consent at any time.
              </li>
            </ul>

            <p className="mb-3">You can exercise these rights by:</p>

            <ul className="list-disc pl-5 space-y-2 mb-4">
              <li>
                Accessing and updating certain information through your account
                settings
              </li>
              <li>
                Contacting our customer support team via email at
                privacy@rideme.com.gh
              </li>
              <li>
                Sending a written request to our physical address listed in the
                "Contact Us" section
              </li>
            </ul>

            <p className="mb-3">
              For marketing communications, you can opt out by:
            </p>

            <ul className="list-disc pl-5 space-y-2 mb-4">
              <li>Clicking the "unsubscribe" link in our emails</li>
              <li>
                Updating your communication preferences in your account settings
              </li>
              <li>Contacting our customer support team</li>
            </ul>

            <p>
              Please note that we may need to verify your identity before
              responding to your requests. We will respond to all legitimate
              requests within the timeframe required by applicable law.
            </p>
          </SectionContent>
        </div>

        {/* Data Protection */}
        <div>
          <SectionHeader id="dataProtection" title="8. Data Security" />
          <SectionContent id="dataProtection">
            <p className="mb-3">
              We implement appropriate technical and organizational measures to
              protect your personal information against unauthorized access,
              alteration, disclosure, or destruction. These measures include:
            </p>

            <ul className="list-disc pl-5 space-y-2 mb-4">
              <li>Encryption of sensitive information</li>
              <li>Secure user authentication procedures</li>
              <li>Regular security assessments and testing</li>
              <li>Access controls and authorization protocols</li>
              <li>
                Employee training on data protection and privacy practices
              </li>
              <li>Physical security measures for our facilities</li>
            </ul>

            <p className="mb-3">
              While we strive to protect your personal information, no method of
              transmission over the Internet or electronic storage is 100%
              secure. Therefore, we cannot guarantee absolute security. We
              encourage you to help protect your personal information by:
            </p>

            <ul className="list-disc pl-5 space-y-2 mb-4">
              <li>Using strong, unique passwords for your RideMe account</li>
              <li>Not sharing your account credentials with others</li>
              <li>Logging out of your account after using shared computers</li>
              <li>
                Being cautious about the information you share in public areas
                of the platform
              </li>
            </ul>

            <p>
              If you suspect any unauthorized access to your account or have
              security concerns, please contact us immediately at
              security@rideme.com.gh.
            </p>
          </SectionContent>
        </div>

        {/* Minors */}
        <div>
          <SectionHeader id="minors" title="9. Children's Privacy" />
          <SectionContent id="minors">
            <p className="mb-3">
              RideMe services are not directed to individuals under 18 years of
              age, and we do not knowingly collect personal information from
              children under 18. To use our platform, you must be at least 18
              years old.
            </p>

            <p className="mb-3">
              If we learn that we have collected or received personal
              information from a child under 18 without verification of parental
              consent, we will take steps to delete that information. If you
              believe we might have any information from or about a child under
              18, please contact us at privacy@rideme.com.gh.
            </p>
          </SectionContent>
        </div>

        {/* Changes */}
        <div>
          <SectionHeader
            id="changes"
            title="10. Changes to This Privacy Policy"
          />
          <SectionContent id="changes">
            <p className="mb-3">
              We may update our Privacy Policy from time to time to reflect
              changes in our practices, technology, legal requirements, and
              other factors. When we make changes, we will:
            </p>

            <ul className="list-disc pl-5 space-y-2 mb-4">
              <li>Post the updated policy on our platform</li>
              <li>Update the "Last Updated" date at the top of this policy</li>
              <li>
                Notify you of material changes via email or in-app notification
              </li>
            </ul>

            <p className="mb-3">
              Your continued use of RideMe after we post changes to the Privacy
              Policy means you accept and agree to the updated policy. If you do
              not agree to the changes, you should discontinue using our
              services.
            </p>

            <p>
              We encourage you to review this Privacy Policy periodically to
              stay informed about how we collect, use, and protect your
              information.
            </p>
          </SectionContent>
        </div>

        {/* Contact */}
        <div>
          <SectionHeader id="contact" title="11. Contact Us" />
          <SectionContent id="contact">
            <p className="mb-3">
              If you have any questions, concerns, or feedback regarding this
              Privacy Policy or our privacy practices, please contact us at:
            </p>

            <div className="pl-5 mb-3">
              <p>
                <strong>The Bakery Technologies LTD</strong>
              </p>
              <p>Email: info@thebakery.tech</p>
              {/* <p>Phone: +233 XX XXX XXXX</p> */}
              <p>Address: 11 Atomic Down Road, Dome-Kwabenya, Accra, Ghana</p>
            </div>

            <p>
              We will respond to your inquiries and concerns as quickly as
              possible.
            </p>
          </SectionContent>
        </div>
      </div>

      <div className="mt-8 p-4 bg-gray-50 rounded-lg border border-gray-200">
        <p className="text-gray-700 text-center">
          By using the xnplatform, you acknowledge that you have read,
          understood, and agree to the terms of this Privacy Policy.
        </p>
      </div>
    </div>
  );
}
